import {useDndContext} from '@github-ui/drag-and-drop'
import {PlusIcon} from '@primer/octicons-react'
import {Octicon} from '@primer/react/deprecated'
import {useCallback, useMemo} from 'react'

import {DropSide, getVerticalDropSide} from '../../../helpers/dnd-kit/drop-helpers'
import type {Row} from '../../../hooks/tanstack-table/helpers/types'
import {useKeyPress} from '../../../state-providers/keypress/key-press-provider'
import type {TableDataType} from '../table-data-type'
import {getSashPosition} from './helpers/drop-helpers'
import {ReorderableRowData} from './reorderable-rows'
import styles from './row-reorder-sash.module.css'

type RowReorderSashProps = {
  rows: Array<Row<TableDataType>>
  isGrouped: boolean
  /** Scrolling container for table rows */
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
  /** Header height used to offset the sash position within the scrolling container. */
  headerHeight?: number
  /** Restrict the sash width to a specific width. Used in roadmap view. */
  tableWidth?: number
}

const SASH_HEIGHT = 3
const SASH_ICON_SIZE = 16
const ROW_BORDER_OFFSET = 1
const SUB_ISSUE_DEPTH_INDENT = 24
const BASE_SUB_ISSUE_INDENT = 92

export const RowReorderSash: React.FC<RowReorderSashProps> = ({
  rows,
  isGrouped,
  scrollRef,
  headerHeight,
  tableWidth,
}) => {
  const {active, over} = useDndContext()
  const {ctrlKey} = useKeyPress()

  const lastOfTypeIndexes = useMemo(() => {
    const last: Array<number> = []
    if (isGrouped) {
      for (const row of rows) {
        const lastId = row.subRows[row.subRows.length - 1]?.original?.id
        if (lastId !== undefined) last.push(lastId)
      }
    } else {
      const lastId = rows[rows.length - 1]?.original?.id
      if (lastId !== undefined) last.push(lastId)
    }
    return last
  }, [rows, isGrouped])

  const isLastOfType = useCallback((overItemId: number) => lastOfTypeIndexes.includes(overItemId), [lastOfTypeIndexes])

  const side = active ? getVerticalDropSide(active, over) : null
  const dropY = side ? getSashPosition(over, side) : null
  const overData = over?.data.current
  const overId = ReorderableRowData.is(overData) ? overData.originalItemId : null
  const lastOfType = overId ? isLastOfType(overId) : false
  const lastOfTypeOffset = side === DropSide.AFTER && lastOfType ? -1 * SASH_HEIGHT + ROW_BORDER_OFFSET : 0

  // Calculate left offset for indented sash when dropping onto a sub-issue position.
  const sashLeftOffset = useMemo(() => {
    if (
      overData &&
      ReorderableRowData.is(overData) &&
      overData.parentItemId !== undefined &&
      overData.subIssueDepth !== undefined &&
      overData.subIssueDepth > 0
    ) {
      if (overData.subIssueDepth === 0) {
        return 0
      }
      return (overData.subIssueDepth - 1) * SUB_ISSUE_DEPTH_INDENT + BASE_SUB_ISSUE_INDENT
    }
    return 0
  }, [overData])

  const cssStyles = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    if (scrollRef?.current && dropY !== null) {
      // eslint-disable-next-line react-hooks/refs
      const rect = scrollRef.current.getBoundingClientRect()
      const headerOffset = headerHeight ?? 0

      // When dropping onto a sub-issue position, indent the sash and reduce width accordingly
      // eslint-disable-next-line react-hooks/refs
      const baseLeft = scrollRef.current.scrollLeft + sashLeftOffset
      const baseWidth = tableWidth ? tableWidth - sashLeftOffset : undefined
      const width = baseWidth !== undefined ? `${baseWidth}px` : `calc(100% - ${sashLeftOffset}px)`

      return {
        // eslint-disable-next-line react-hooks/refs
        top: scrollRef.current.scrollTop - rect.top - headerOffset + lastOfTypeOffset + dropY - ROW_BORDER_OFFSET,
        left: baseLeft,
        width,
      }
    }
    return {}
  }, [scrollRef, tableWidth, headerHeight, dropY, lastOfTypeOffset, sashLeftOffset])

  return (
    <>
      {Boolean(active && over && dropY !== null && overId && side && active.id !== over.id) && (
        <div
          style={{
            height: SASH_HEIGHT,

            ...cssStyles,
          }}
          className={styles.Box}
        >
          {isGrouped && ctrlKey ? <Octicon icon={PlusIcon} size={SASH_ICON_SIZE} className={styles.Octicon} /> : null}
        </div>
      )}
    </>
  )
}
